<template>
  <FilterGroup
    v-if="modelValue"
    :badge="modelValue.length"
    :label="filter.name"
    :is-collapsible="isCollapsible"
    :filter-slug="filter.slug"
    @click:reset="handleReset"
    @click:apply="handleApply"
    @reset-to-initial-state="handleResetToInitialState"
  >
    <div
      v-for="(groupName, index) in sizeGroups"
      :key="index"
      :class="{ 'mt-7': index > 0 }"
    >
      <h3
        :id="`size-group-heading-${index}`"
        class="h-6 pr-2.5 leading-6 focus:mt-2 focus:outline focus:outline-2 focus:outline-primary"
        tabindex="0"
      >
        {{ groupName }}
      </h3>
      <MultipleSelectionList
        :model-value="tempModelValue"
        :items="getSizesForSizeGroupName(groupName)"
        :use-deep-is-active-check="true"
        :initial-state="modelValue"
        :is-reset="isReset"
        class="mt-3 flex flex-wrap gap-4"
        name="FilterSelectSize"
        @update:model-value="handleModelValueUpdate"
      >
        <template #item="{ item, toggleItem, isActive }">
          <div v-if="item?.isSeparator" class="w-full" />
          <button
            v-else
            :class="{ 'ring-1 ring-gray-secondary': isActive }"
            class="px-1 text-base font-normal leading-5"
            @click="toggleItem(item)"
          >
            {{ item.displayName.split('|')[1] }}
          </button>
        </template>
      </MultipleSelectionList>
    </div>
  </FilterGroup>
</template>

<script setup lang="ts">
import type {
  TransformedFilter,
  AttributesFilterItemWithValues,
} from '@scayle/storefront-nuxt'
import type { AttributesFilterValue } from '#layers/base/utils/filter'
import FilterGroup from '~/components/filters/FilterGroup.vue'
import MultipleSelectionList from '#layers/base/components/ui/selectionList/MultipleSelectionList.vue'
import {
  BROKEN_FILTER_IDS_PREV,
  BROKEN_FILTER_IDS_PROD,
  CUP_ATTRIBUTE_IDS_PREV,
  CUP_ATTRIBUTE_IDS_PROD,
} from '#layers/base/constants/filters'
import { useDeviceType } from '~/composables/useDeviceType'
import {
  SizeCategoryIdentifier,
  parseSizeCategorySortOrder,
  sortInternationalSizes,
} from '#layers/base/utils/filter'

// Fallback sort order used when no override is supplied via
// `shopLevelCustomData.sizeCategorySortOrder` on the active PLP category.
const categorySortOrder = [
  SizeCategoryIdentifier.NGROESSEN,
  SizeCategoryIdentifier.KGROESSEN,
  SizeCategoryIdentifier.EKGROESSEN,
  SizeCategoryIdentifier.LGROESSEN,
  SizeCategoryIdentifier.KOERBCHENGROESSEN,
  SizeCategoryIdentifier.BADEMODENGROESSEN,
  SizeCategoryIdentifier.MIEDERGROESSEN_STANDARDLAUF,
  SizeCategoryIdentifier.MIEDERGROESSEN_GESAMT,
  SizeCategoryIdentifier.SCHUHGROESSEN,
  SizeCategoryIdentifier.GROESSEN,
]

const categoryRegex: RegExp = new RegExp(
  `(${Object.values(SizeCategoryIdentifier).join('|')})`,
  'g',
)

const getSizeSizeCategoryIdentifier = (
  filter: AttributesFilterItemWithValues,
  forValue: TransformedFilter,
): SizeCategoryIdentifier => {
  const identifier = filter.values.find(
    (fv: AttributesFilterValue) => fv.id === forValue.value,
  )
  return (
    (identifier?.value?.match(categoryRegex)?.[0] as SizeCategoryIdentifier) ||
    SizeCategoryIdentifier.UNKNOWN
  )
}

type Props = {
  modelValue: any[]
  filter: AttributesFilterItemWithValues
  filterValues: TransformedFilter[]
  isCollapsible?: boolean
  ungroupedFilterValues: AttributesFilterValue[]
  sizeCategorySortOrderOverride?: string | string[] | null
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: () => [],
  isCollapsible: false,
  sizeCategorySortOrderOverride: null,
})

const effectiveSortOrder = computed<SizeCategoryIdentifier[]>(() => {
  const overrideOrder = parseSizeCategorySortOrder(
    props.sizeCategorySortOrderOverride,
  )
  return overrideOrder.length > 0 ? overrideOrder : categorySortOrder
})

const emit = defineEmits([
  'reset',
  'apply',
  'update:modelValue',
  'update:modelValueIds',
])

const config = useRuntimeConfig()
const isReset = ref<boolean>(false)
const { showNewDesktopPlpQuickFilters } = useExperiments()
const { isMobileOrTablet } = useDeviceType()
const tempModelValue = ref(props.modelValue)

const cupAttributeIds =
  config.public.appEnv === 'production'
    ? CUP_ATTRIBUTE_IDS_PROD
    : CUP_ATTRIBUTE_IDS_PREV

const brokenFilterIds =
  config.public.appEnv === 'production'
    ? BROKEN_FILTER_IDS_PROD
    : BROKEN_FILTER_IDS_PREV

const idsToFilter = [...cupAttributeIds, ...brokenFilterIds]

const filteredFilterValues = computed<TransformedFilter[]>(() => {
  return props.filterValues.filter(
    (f) => !idsToFilter.includes(f.value) && !!f.displayName.split('|')[1],
  )
})
// Returns a numeric rank for an identifier, falling back through:
//   1. its position in the (possibly partial) override sort order,
//   2. its position in the hard-coded `categorySortOrder`,
//   3. `Infinity` (i.e. anything still unknown sorts to the end).
// This guarantees the override can be partial without sending unmapped
// categories to the front of the list (where `indexOf === -1` would put them).
const getCategoryRank = (identifier: SizeCategoryIdentifier): number => {
  const overrideIndex = effectiveSortOrder.value.indexOf(identifier)
  if (overrideIndex !== -1) {
    return overrideIndex
  }

  const fallbackIndex = categorySortOrder.indexOf(identifier)
  if (fallbackIndex !== -1) {
    // Offset so any explicitly-ranked override entry always wins over a
    // fallback-ranked one, regardless of override length.
    return effectiveSortOrder.value.length + fallbackIndex
  }

  return Number.POSITIVE_INFINITY
}

const filterValuesSortedByGroup = computed<TransformedFilter[]>(() => {
  return [...filteredFilterValues.value].sort((a, b) => {
    const rankA = getCategoryRank(
      getSizeSizeCategoryIdentifier(props.filter, a),
    )
    const rankB = getCategoryRank(
      getSizeSizeCategoryIdentifier(props.filter, b),
    )
    // Equal ranks (incl. two unknown categories that both resolve to
    // `+Infinity`) must short-circuit, otherwise `Infinity - Infinity === NaN`
    // would yield non-deterministic ordering. Returning 0 preserves the input
    // order of the equal entries via the engine's stable sort.
    if (rankA === rankB) {
      return 0
    }
    return rankA - rankB
  })
})

const handleReset = () => {
  isReset.value = false
  tempModelValue.value = props.modelValue

  nextTick(() => {
    isReset.value = true
    emit('reset')
  })
}

const handleResetToInitialState = () => {
  isReset.value = false
  tempModelValue.value = props.modelValue

  nextTick(() => {
    isReset.value = true
  })
}

const handleApply = () => {
  isReset.value = false
  emit('update:modelValue', tempModelValue.value)
  emit(
    'update:modelValueIds',
    tempModelValue.value.map((item: any) => item.value),
  )
  emit('apply', {
    values: tempModelValue.value.map((item: any) => item.value),
    displayNames: tempModelValue.value.map((item: any) => item.displayName),
  })
}

const handleModelValueUpdate = (newValue: any[]) => {
  tempModelValue.value = newValue

  if (
    !showNewDesktopPlpQuickFilters.value ||
    (showNewDesktopPlpQuickFilters.value && isMobileOrTablet.value)
  ) {
    emit('update:modelValue', tempModelValue.value)
    emit(
      'update:modelValueIds',
      tempModelValue.value.map((item: any) => item.value),
    )
  }
}

watch(
  () => props.modelValue,
  (newModelValue) => {
    tempModelValue.value = newModelValue
  },
  { deep: true },
)

const sizeGroups = computed(() => [
  ...new Set(
    filterValuesSortedByGroup.value
      .map((value) => value.displayName.split('|')[0])
      .filter((name): name is string => name !== undefined),
  ),
])
const getSizesForSizeGroupName = (name: string) => {
  const filtered = filteredFilterValues.value.filter(
    (value) => value.displayName.split('|')[0] === name,
  )

  // Check if these are shoe sizes
  const ungrouped = props.ungroupedFilterValues.filter(
    (value) => value.name.split('|')[0] === name,
  )
  const areShoeSizes = ungrouped.some((item) =>
    item?.value?.toLowerCase().includes(SizeCategoryIdentifier.SCHUHGROESSEN),
  )

  if (areShoeSizes) {
    return separateShoeSizes(filtered)
  }

  return sortInternationalSizes(filtered)
}

// adds a separator between EU and US sizes
const separateShoeSizes = (sizes: { displayName: string }[]) => {
  const sizesWithNumericValue = sizes.map((size) => {
    const numericSize = size.displayName.split('|')[1]
    return numericSize === undefined
      ? undefined
      : { size, numericSize: parseFloat(numericSize.replace(',', '.')) }
  })

  if (sizesWithNumericValue.some((item) => item === undefined)) {
    return sizes
  }

  const validSizes = sizesWithNumericValue.filter(
    (item): item is { size: { displayName: string }; numericSize: number } =>
      item !== undefined,
  )
  const euSizes = validSizes
    .filter(({ numericSize }) => numericSize > 17)
    .map(({ size }) => size)
  const usSizes = validSizes
    .filter(({ numericSize }) => numericSize <= 17)
    .map(({ size }) => size)

  return [...euSizes, { displayName: '', isSeparator: true }, ...usSizes]
}
</script>
